import { useState, useEffect, useContext } from "react";
import { ThemeContext } from "./ThemeContext.jsx";

// ─── WHAT'S NEW — ADMIN CONTROLLED ───────────────────────────────────────────
//
// TO ADD NEW UPDATES:
//   1. Add items to the UPDATES array below (newest first)
//   2. Bump the WHATS_NEW_VERSION string (use the deploy date: "v2026-MM-DD")
//   3. Deploy — every user will see the popup fresh on their next login
//
// Each update item:
//   emoji  — one emoji to show as the icon
//   title  — short headline (keep under ~40 chars)
//   desc   — one sentence description
//   tag    — optional pill label: "new" | "fix" | "update" | "memory" | "coming soon"
//
// ─────────────────────────────────────────────────────────────────────────────

const WHATS_NEW_VERSION = "v2026-10-19-v423";

const UPDATES = [
  {
    emoji: "💌",
    title: "Who may text you first",
    desc: "Random DMs now have four settings: everyone, everyone except the ones you untick, only the ones you pick, or never. Pick from the 💌 button above your character DMs, the 💬 switch on a DM row, or Settings → App & AI. A character only texts into a conversation you are actually having, and only after reading your whole history together.",
    tag: "new",
  },
  {
    emoji: "📜",
    title: "Lore rooms and story lore",
    desc: "Any channel can be a Lore room (community or Our Channels), the Lorekeeper answers from them, and every story and DM has its own Lore note that the author keeps.",
    tag: "new",
  },
  {
    emoji: "🪪",
    title: "Profile cards",
    desc: "Tap any avatar in a channel or a DM to see that person's profile.",
    tag: "new",
  },
  {
    emoji: "🔔",
    title: "Notification settings on iPhone",
    desc: "Every message vs. bundled, the category switches and Snooze now show in the iPhone app too, not only on the web.",
    tag: "fix",
  },
  {
    emoji: "🧠",
    title: "Characters now remember everything",
    desc: "Full story memory system — characters recall past scenes, DMs, promises, and intimate moments across every conversation.",
    tag: "memory",
  },
  {
    emoji: "📜",
    title: "Deep history timeline",
    desc: "Long conversations now get an importance-scored timeline — key quotes and moments are preserved even from the very beginning of a story.",
    tag: "memory",
  },
  {
    emoji: "🔒",
    title: "Your API key is always yours",
    desc: "All story responses now always go through your personal API key. Global key is background-only.",
    tag: "update",
  },
  {
    emoji: "🌶️",
    title: "Adult content memory unlocked",
    desc: "Characters remember intimate scenes as part of the story arc — no fade to black, no disclaimers. Requires your personal key.",
    tag: "new",
  },
  {
    emoji: "💬",
    title: "Incognito mode gets real AI responses",
    desc: "Messages on resourcehub.cloud now get full character replies — same quality as the main DM view.",
    tag: "fix",
  },
  {
    emoji: "🎨",
    title: "5 new themes",
    desc: "Cotton Candy, Candy Blue, Blood Moon, Tidewater, and Candy Pink — find them in Profile → Appearance.",
    tag: "new",
  },
];

// ─── TAG COLORS ──────────────────────────────────────────────────────────────
const TAG_STYLES = {
  new:          { bg: "rgba(100,220,130,0.15)", color: "#5dce80",  label: "New" },
  fix:          { bg: "rgba(255,180,50,0.15)",  color: "#ffb432",  label: "Fixed" },
  update:       { bg: "rgba(100,180,255,0.15)", color: "#64b4ff",  label: "Updated" },
  memory:       { bg: "rgba(200,130,255,0.15)", color: "#c882ff",  label: "Memory" },
  "coming soon":{ bg: "rgba(180,180,180,0.12)", color: "#999",     label: "Soon" },
};

// ─────────────────────────────────────────────────────────────────────────────

const DISMISS_KEY = `mfml-whats-new-${WHATS_NEW_VERSION}`;

export default function AnnouncementModal({ _onOpenAppearance }) {
  const themeContext = useContext(ThemeContext);
  const t = themeContext?.theme || {};

  const [visible, setVisible] = useState(false);
  const [closing, setClosing] = useState(false);

  useEffect(() => {
    try {
      const dismissed = localStorage.getItem(DISMISS_KEY);
      if (!dismissed) {
        const timer = setTimeout(() => setVisible(true), 1400);
        return () => clearTimeout(timer);
      }
    } catch {}
  }, []);

  const dismiss = () => {
    setClosing(true);
    setTimeout(() => {
      setVisible(false);
      setClosing(false);
      try { localStorage.setItem(DISMISS_KEY, "1"); } catch {}
    }, 260);
  };

  if (!visible) return null;

  const bgCard   = t.bgSecondary  || "#1e1f26";
  const textMain = t.textPrimary  || "#f0f0f0";
  const textSub  = t.textSecondary || "#b0b4bc";
  const border   = "rgba(255,255,255,0.08)";

  return (
    <div
      onClick={dismiss}
      style={{
        position: "fixed", inset: 0, zIndex: 9100,
        background: "rgba(0,0,0,0.70)", backdropFilter: "blur(5px)",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: "16px",
        animation: closing ? "wnFadeOut 0.26s ease forwards" : "wnFadeIn 0.3s ease",
      }}
    >
      <style>{`
        @keyframes wnFadeIn  { from { opacity:0; transform:translateY(12px) scale(0.96) } to { opacity:1; transform:translateY(0) scale(1) } }
        @keyframes wnFadeOut { from { opacity:1; transform:scale(1) } to { opacity:0; transform:scale(0.95) } }
      `}</style>

      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: bgCard,
          borderRadius: "18px",
          padding: "26px 22px 22px",
          maxWidth: "440px",
          width: "100%",
          maxHeight: "85vh",
          overflowY: "auto",
          boxShadow: "0 28px 70px rgba(0,0,0,0.65), 0 0 0 1px rgba(255,255,255,0.07)",
          position: "relative",
          animation: closing ? "wnFadeOut 0.26s ease forwards" : "wnFadeIn 0.3s ease",
        }}
      >
        {/* Close (X) button */}
        <button
          onClick={dismiss}
          aria-label="Dismiss"
          style={{
            position: "absolute", top: "14px", right: "14px",
            background: "rgba(255,255,255,0.07)", border: "none",
            borderRadius: "50%", width: "30px", height: "30px",
            color: textSub, cursor: "pointer",
            fontSize: "18px", lineHeight: 1,
            display: "flex", alignItems: "center", justifyContent: "center",
            transition: "background 0.15s",
          }}
          onMouseEnter={e => e.currentTarget.style.background = "var(--ds-hover)"}
          onMouseLeave={e => e.currentTarget.style.background = "var(--ds-glass-light)"}
        >
          ×
        </button>

        {/* Header */}
        <div style={{ marginBottom: "20px", paddingRight: "32px" }}>
          <div style={{
            fontSize: "11px", fontWeight: 700, letterSpacing: "1px",
            textTransform: "uppercase", color: t.accent || "#c882ff",
            marginBottom: "6px",
          }}>
            ✦ What's New
          </div>
          <h2 style={{
            margin: 0, fontSize: "20px", fontWeight: 800,
            color: textMain, lineHeight: 1.25,
          }}>
            Latest Updates
          </h2>
          <p style={{
            margin: "6px 0 0", fontSize: "12px",
            color: textSub, lineHeight: 1.5,
          }}>
            Here's what changed since your last visit.
          </p>
        </div>

        {/* Update list */}
        <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
          {UPDATES.map((item, i) => {
            const tagStyle = item.tag ? TAG_STYLES[item.tag] : null;
            return (
              <div
                key={i}
                style={{
                  display: "flex", gap: "12px", alignItems: "flex-start",
                  padding: "12px 13px",
                  background: "rgba(255,255,255,0.03)",
                  border: `1px solid ${border}`,
                  borderRadius: "12px",
                }}
              >
                {/* Emoji icon */}
                <div style={{
                  fontSize: "22px", lineHeight: 1, flexShrink: 0,
                  marginTop: "1px", width: "28px", textAlign: "center",
                }}>
                  {item.emoji}
                </div>

                {/* Text */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "7px", flexWrap: "wrap", marginBottom: "3px" }}>
                    <span style={{
                      fontSize: "13px", fontWeight: 700,
                      color: textMain, lineHeight: 1.3,
                    }}>
                      {item.title}
                    </span>
                    {tagStyle && (
                      <span style={{
                        fontSize: "9px", fontWeight: 700, letterSpacing: "0.4px",
                        textTransform: "uppercase",
                        background: tagStyle.bg, color: tagStyle.color,
                        borderRadius: "6px", padding: "2px 7px",
                        flexShrink: 0,
                      }}>
                        {tagStyle.label}
                      </span>
                    )}
                  </div>
                  <p style={{
                    margin: 0, fontSize: "12px",
                    color: textSub, lineHeight: 1.5,
                  }}>
                    {item.desc}
                  </p>
                </div>
              </div>
            );
          })}
        </div>

        {/* Footer */}
        <div style={{
          marginTop: "18px", display: "flex", alignItems: "center",
          justifyContent: "space-between", gap: "10px",
        }}>
          <span style={{ fontSize: "11px", color: textSub }}>
            {new Date().toLocaleDateString("en-US", { month: "long", year: "numeric" })}
          </span>
          <button
            onClick={dismiss}
            style={{
              background: t.accent ? `${t.accent}22` : "rgba(200,130,255,0.15)",
              color: t.accent || "#c882ff",
              border: `1px solid ${t.accent ? `${t.accent}44` : "rgba(200,130,255,0.3)"}`,
              borderRadius: "10px",
              padding: "9px 20px",
              fontSize: "13px", fontWeight: 700,
              cursor: "pointer", transition: "opacity 0.15s",
            }}
            onMouseEnter={e => e.currentTarget.style.opacity = "0.8"}
            onMouseLeave={e => e.currentTarget.style.opacity = "1"}
          >
            Got it ✓
          </button>
        </div>
      </div>
    </div>
  );
}
